import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Input,
  InputProps,
  NonCancelableCustomEvent,
  Spinner,
} from '@amzn/awsui-components-core';
import {
  Collection,
  Publication,
  ContentType,
  FeedbackItemStatistics,
} from '@amzn/awscat-pattern-library-service-model';
import arraySort from 'array-sort';

import CommunityPageBanner from './CommunityPageBanner';
import { IMplServiceClient } from '../../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import CommunityCollectionCtx from './CommunityCollectionCtx';
import { Filters } from '../../content/ContentFilters';
import CollectionFilters from './CollectionFilters';
import CollectionCardsView from './CollectionCardsList';
import CollectionSortDropDown from './CollectionSortDropDown';
import { mergeCollectionsWithMetadata } from './utils';
import { IFeedbackServiceClient, FeedbackServiceFactory } from '../../../api/FeedbackService';
import { FeedbackRatingSystem, FeedbackSource } from '../../feedback-service';
import CollectionFilterSlicer from './CollectionFilterSlicer';
import { FilterCategory } from '../../content/ContentFilters';

export interface CollectionWithTemplateMetaData extends Collection {
  templateMetadata?: any;
  hasRated?: boolean;
  like?: number;
  dislike?: number;
}

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
const feedbackServiceClient: IFeedbackServiceClient = FeedbackServiceFactory.create();

const CommunityCollection = () => {
  const [filteredCollections, setFilteredCollections] = useState<Collection[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [allCollections, setAllCollections] = useState<Collection[]>([]);
  const [filterOptions, setFilterOptions] = useState<Filters>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isFetchingCollections, setIsFetchingCollections] = useState<boolean>(false);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [isFetchingPublications, setIsFetchingPublications] = useState<boolean>(false);
  const [feedbackItems, setFeefbackItems] = useState<(FeedbackItemStatistics | null)[]>([]);
  const [collectionLikeDislikeFeedback, setCollectionLikeDislikeFeedback] = useState<
    (FeedbackItemStatistics | null)[]
  >([]);

  useEffect(() => {
    const fetchCollections = async () => {
      setIsFetchingCollections(true);
      const response = await mplServiceClient.listPublicCollections();
      arraySort(response.collections, 'createdAt', { reverse: true });
      setCollections(response.collections);
      setAllCollections(response.collections);
      setFilteredCollections(response.collections);
      setIsFetchingCollections(false);
    };
    fetchCollections();
  }, []);

  useEffect(() => {
    const fetchPublications = async () => {
      setIsFetchingPublications(true);
      const publicationLists = await mplServiceClient.listPublicationsThroughS3();
      const validPublications = publicationLists.publications.filter((publication) =>
        [ContentType.Pattern, ContentType.Guide, ContentType.Strategy].includes(
          publication.contentMetadata.contentType,
        ),
      );
      setPublications(validPublications);
    };
    fetchPublications();
  }, []);

  useEffect(() => {
    if (publications.length && allCollections.length) {
      const collectionsWithMetadata = mergeCollectionsWithMetadata(collections, publications);
      setCollections(collectionsWithMetadata);
      setAllCollections(collectionsWithMetadata);
      setFilteredCollections(collectionsWithMetadata);
      setIsFetchingPublications(false);
    }
  }, [publications, allCollections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchFeedback = async () => {
      const resourceIds = allCollections.map((collection) => collection.collectionId);
      const response = await feedbackServiceClient.listFeedback(
        resourceIds,
        FeedbackRatingSystem.Like,
      );
      setFeefbackItems(response);
    };

    if (allCollections.length) {
      fetchFeedback();
    }
  }, [allCollections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchFeedback = async () => {
      const resourceIds = allCollections.map((collection) => collection.collectionId);
      const collectionsFeedback = await feedbackServiceClient.listFeedback(
        resourceIds,
        FeedbackRatingSystem.LikeDislike,
        FeedbackSource.Collection,
      );
      setCollectionLikeDislikeFeedback(collectionsFeedback);
    };

    if (allCollections.length) {
      fetchFeedback();
    }
  }, [allCollections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const allCollectionsWithFeedback = allCollections.map((collection, index) => {
      return {
        ...collection,
        hasRated: feedbackItems[index]?.hasRated,
      };
    });
    setAllCollections(allCollectionsWithFeedback);
    setCollections(allCollectionsWithFeedback);
  }, [feedbackItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const allCollectionsWithFeedback = allCollections.map((collection, index) => {
      const counts = collectionLikeDislikeFeedback[index]?.counts ?? [];
      return {
        ...collection,
        like: counts[0]?.count ?? 0,
        dislike: counts[1]?.count ?? 0,
      };
    });
    setAllCollections(allCollectionsWithFeedback);
    setCollections(allCollectionsWithFeedback);
  }, [collectionLikeDislikeFeedback]); // eslint-disable-line react-hooks/exhaustive-deps

  const communityCollectionCtxData = {
    filteredCollections,
    setFilteredCollections,
    filterOptions,
    setFilterOptions,
    allCollections,
    searchTerm,
  };

  const displayFilterCount = () => {
    if (filteredCollections.length === allCollections.length) {
      return allCollections.length;
    }
    return `${filteredCollections.length} of ${allCollections.length}`;
  };

  const handleSearchChange = async (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    setSearchTerm(event.detail.value);
    if (event.detail.value?.length) {
      const searchedCollections = allCollections.filter((collection) => {
        const { title, description } = collection;
        const searchWordLowerCase = event.detail.value.toLocaleLowerCase();
        return (
          title.toLocaleLowerCase().includes(searchWordLowerCase) ||
          description.toLocaleLowerCase().includes(searchWordLowerCase)
        );
      });
      setCollections(searchedCollections);
      setFilteredCollections(searchedCollections);
    } else {
      setCollections(allCollections);
      setFilteredCollections(allCollections);
    }
  };

  return (
    <CommunityCollectionCtx.Provider value={communityCollectionCtxData}>
      <CommunityPageBanner />
      <Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
        {isFetchingPublications ? <Spinner /> : <CollectionFilters collections={collections} />}
        <div className="community-collection-view-container">
          <div className="community-collection-search-bar">
            <Box variant="h3" padding={{ top: 's', bottom: 's' }} fontSize="heading-m">
              Collections ({displayFilterCount()})
            </Box>
            <div className="community-collection-search-container">
              <Input
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search collections"
                type="search"
                ariaRequired
              />
            </div>
          </div>
          <div className="community-collection-sort-bar">
            <div>
              <CollectionFilterSlicer filterType={FilterCategory.IsFavoriteCollection} />
            </div>
            <CollectionSortDropDown />
          </div>
          {isFetchingCollections ? <Spinner /> : <CollectionCardsView />}
        </div>
      </Grid>
    </CommunityCollectionCtx.Provider>
  );
};

export default CommunityCollection;
